import React, { useCallback, useEffect, useRef } from 'react';

const ML_WIDGET_SRC = 'https://media-library.cloudinary.com/global/all.js';

/**
 * CloudinaryImagePicker — wraps Cloudinary's Media Library Widget.
 *
 * Browses your own Cloudinary media library (uploaded assets).
 * If REACT_APP_CLOUDINARY_CLOUD_NAME / REACT_APP_CLOUDINARY_API_KEY are not set,
 * falls back to a simple local file picker.
 *
 * Props:
 *  - onSelect({ publicId, secureUrl, credit })  – called when user picks an image
 *  - open       – boolean, when true the widget is shown
 *  - onClose    – called when the widget closes
 *  - folder     – optional default browse folder (e.g. 'alchemiq-social/brand-images')
 */
export default function CloudinaryImagePicker({ onSelect, open, onClose, folder }) {
  const widgetRef = useRef(null);
  const scriptLoaded = useRef(false);
  const fileInputRef = useRef(null);
  // Keep stable refs to avoid useEffect re-triggering from parent re-renders
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY;
  const hasCloudinary = !!(cloudName && apiKey);

  const ensureScript = useCallback(() => {
    return new Promise((resolve) => {
      if (scriptLoaded.current || window.cloudinary?.createMediaLibrary) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = ML_WIDGET_SRC;
      script.onload = () => {
        scriptLoaded.current = true;
        resolve();
      };
      document.head.appendChild(script);
    });
  }, []);

  const handleLocalFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (onSelectRef.current) {
        onSelectRef.current({
          publicId: null,
          secureUrl: reader.result,
          credit: { photographer: '', source: 'Local Upload', url: '' },
        });
      }
      if (onCloseRef.current) onCloseRef.current();
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Only react to `open` changing to true — stable refs prevent re-triggering
  useEffect(() => {
    if (!open) return;

    if (!hasCloudinary) {
      // Fallback: open native file picker
      fileInputRef.current?.click();
      return;
    }

    // Cloudinary widget path
    (async () => {
      await ensureScript();

      if (!widgetRef.current) {
        widgetRef.current = window.cloudinary.createMediaLibrary(
          {
            cloud_name: cloudName,
            api_key: apiKey,
            multiple: false,
            max_files: 1,
            insert_caption: 'Select',
            default_transformations: [],
            folder: folder ? { path: folder, resource_type: 'image' } : undefined,
          },
          {
            insertHandler: (data) => {
              const asset = data?.assets?.[0];
              if (asset && onSelectRef.current) {
                onSelectRef.current({
                  publicId: asset.public_id,
                  secureUrl: asset.secure_url || asset.url,
                  credit: {
                    photographer: asset.context?.custom?.photographer || '',
                    source: asset.context?.custom?.source || 'Cloudinary',
                    url: asset.context?.custom?.source_url || '',
                  },
                });
              }
              if (onCloseRef.current) onCloseRef.current();
            },
          }
        );
      }

      widgetRef.current.show();
    })();
  }, [open, hasCloudinary, cloudName, apiKey, folder, ensureScript]);

  // Hidden file input for fallback mode (no Cloudinary env vars)
  return (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={handleLocalFile}
    />
  );
}
